import { Validators } from "@angular/forms"
import { ColorOverlayProperties, Form, FormType, Option, OverlayProperties, SharedProperties } from "./properties.model"
import { EffectsType } from "./ui-effects.model"
import FastNoiseLite from "fastnoise-lite-typed"


export enum LayerType {
  MaterialOverlay = 'Material Overlay',
  Buildings = 'Buildings',
  Overlay = 'Overlay',
  Contour = 'Contour',
  Roads = 'Roads',
  Base = 'Base',
  Water = 'Water',
  POIGlow = 'POI Glow',
  HeightGlow = 'Height Glow',
  GridLines = 'Grid Lines',
  Zones = 'Zones',
}

export interface Layer {
  id: string,
  name: string,
  layerType: LayerType
  effects: Effects[]
  sharedSettings: LayerSettings
  effectSettings: EffectSettings
  buildingSettings?: BuildingSettings
  roadSettings?: RoadSettings
  colorOverlaySettings?: ColorOverlaySettings
  contourSettings?: ContourSettings
  poiGlowSettings?: PoiGlowSettings
  gridLinesSettings?: GridLinesSettings
  waterSettings?: WaterSettings
  heightGlowSettings?: HeightGlowSettings
  materialOverlaySettings?: MaterialOverlaySettings
  zoneSettings?: ZoneSettings
  baseLayerSettings?: BaseSettings
}

export interface Effects {
  id: string,
  name: string,
  effectType: EffectsType
}

export interface LayerSettings {
  opacity: number
  visible: boolean
  showEffects: boolean
  blendingMode: BlendingOption
  selectionBlur: {
    blurSize: number
    blurSpread: number
  }
}

export interface EffectSettings {
  outlineSetttings: OutlineSettings
  blurSettings: BlurSettings
  textureSettings: TextureSettings
  sepiaSettings: SepiaSettings
  dropShadowSettings: DropShadowSettings
  noiseSettings: NoiseSettings
}

export interface GridLinesSettings {
  style: GridOption
  color: string
  opacity: number
  density: number
  offsetX: number
  offsetY: number
}

export enum GridSettingsType {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
  Grid = 'grid',
  Hex = 'hex',
}

export interface GridOption extends Option {
  value: GridSettingsType
}

export interface NoiseOption extends Option {
  value: FastNoiseLite.NoiseType
}

export interface NoiseFractileOption extends Option {
  value: FastNoiseLite.FractalType
}

export interface NoiseSettings {
  noiseType: NoiseOption
  noiseBlendingMode: BlendingOption
  noiseHideUnderlyingLayer: boolean
  noiseFrequency: number
  noiseOpacity: number
  noiseInvertMask: boolean
  noiseColorGradient: GradientHandle[]
  noiseSeed: number
  noiseFractileOctives: number
  noiseFractileGain: number
  noiseFractileLacunarity: number
  noiseFractileType: NoiseFractileOption
}

export interface DropShadowSettings {
  dropShadowOffsetX: number
  dropShadowOffsetY: number
  dropShadowLength: number
  dropShadowColor: string
}

export interface TextureSettings {
  textureColor: string
  textureSelector: Option
  textureOpacity: number
  textureScale: number
  textureHideUnderlyingLayer: boolean
}

export interface BlurSettings {
  blurStrength: number
}

export interface SepiaSettings {
  sepiaStrength: number
}

export interface OutlineSettings {
  outlineInnerOutline: boolean
  outlineColor: string
  outlineWidth: number
  outlineOpacity: number
}

export interface LayerList {
  [key: string]: Layer
}

export interface ColorOverlaySettings {
  color: string
}

export interface WaterSettings{
  waterColor: GradientHandle[]
  waterOpaqueDepth: number
  waterMinOpacity: number
}

export interface HeightGlowSettings {
  heightGlowColor: GradientHandle[]
  heightGlowRange: number[]
  heightGlowRamp: number
}

export interface BaseSettings {
  emptyPixelColor: string
  emptyPixelOpacity: number
}

export interface ZoneSettings {
  zoneGroup: Option[],
  zoneDetail: number
  zoneOffset: number
  zoneFillEnabled: boolean
  zoneFillColor: string
  zoneFillOpacity: number
  zoneStrokeEnabled: boolean
  zoneStrokeWidth: number
  zoneStrokeColor: string
  zoneStrokeOpacity: number
  zoneStrokeDashed: boolean
  zoneStrokeDashLength: number
}

export interface GradientHandle {
  color: string
  position: number
}

export interface ContourSettings {
  contourTotalLines: number
  contourVisibleRange: number[]
  contourResolution: number
  contourLineWidth: number
  contourLineColor: GradientHandle[]
}

export interface PoiGlowSettings {
  poiGlowColor: GradientHandle[]
  poiGlowStrength: number
  poiGlowWidth: number
  poiShadowOpacity: number
}

export interface BuildingSettings {
  buildingGroup: Option[],
  buildingColor: string
  buildingColorOpacity: number
  buildingWeatheredRoof: boolean
  buildingWeatheredRoofVariation: number
}
export interface RoadSettings {
  roadGroup: Option[],
  roadColor: string
  roadColorOpacity: number
}

export interface MaterialOverlaySettings {
  materialOverlaySelector: Option[],
  materialOverlayColor: string
  materialOverlayOpacity: number
}

export interface BlendingOption extends Option {
  value: GlobalCompositeOperation
}

export const blendingTypes: BlendingOption[] = [
  { label: 'Normal', value: 'source-over'},
  { label: 'Multiply', value: 'multiply' },
  { label: 'Screen', value: 'screen' },
  { label: 'Overlay', value: 'overlay' },
  { label: 'Darken', value: 'darken' },
  { label: 'Lighten', value: 'lighten' },
  { label: 'ColorDodge', value: 'color-dodge' },
  { label: 'ColorBurn', value: 'color-burn' },
  { label: 'HardLight', value: 'hard-light' },
  { label: 'SoftLight', value: 'soft-light' },
  { label: 'Difference', value: 'difference' },
  { label: 'Exclusion', value: 'exclusion' },
  { label: 'Hue', value: 'hue' },
  { label: 'Saturation', value: 'saturation' },
  { label: 'Color', value: 'color' },
  { label: 'Luminosity', value: 'luminosity' },
] as const

export const sharedForms: Form[] = [
  {
    id: SharedProperties.Name,
    label: 'Layer Name',
    type: FormType.Text,
    placeholder: 'Layer 2',
    validators: [Validators.required, Validators.minLength(1)]
  },
  {
    id: SharedProperties.BlendingMode,
    label: 'Blending Mode',
    type: FormType.Select,
    options: blendingTypes,
    hint: "Change the way this layer interacts with other layers. Particularly useful ones I enjoy: Overlay, SoftLight, Screen, Multiply",
    defaultValue: {label: 'Normal', value: 'normal'}
  },
  {
    id: SharedProperties.Opacity,
    label: 'Layer Opacity',
    type: FormType.SliderNumber,
    sliderOptions: {
      min: 0,
      max: 1,
      step: 0.01
    }
  },
] as const

export const overlaySelectionForm: Form[] = [
  {
    id: OverlayProperties.BlurSize,
    label: 'Size',
    type: FormType.SliderNumber,
    defaultValue: 0,
    sliderOptions: {
      min: .01,
      max: 1,
      step: 0.01
    }
  },
  {
    id: OverlayProperties.BlurSpread,
    label: 'Spread',
    type: FormType.SliderNumber,
    defaultValue: 0,
    sliderOptions: {
      min: .01,
      max: 1,
      step: 0.01
    }
  },
]

export const overlayBlur: Form = {
  id: "container",
  type: FormType.Container,
  hint: "These settings are to help refine and smooth the material selection",
  label: "Selection Blur",
  children: overlaySelectionForm
}
